import { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import React from 'react';

interface STLViewerProps {
  file: File;
  onVolumeCalculated?: (volume: number) => void;
}

function STLViewer({ file, onVolumeCalculated }: STLViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const hasInteracted = useRef(false);
  const volumeCalculatedRef = useRef(false);
  const animationFrameRef = useRef<number>();
  const meshRef = useRef<THREE.Mesh | null>(null);
  const mountedRef = useRef(true);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);

  const animate = useCallback(() => {
    if (!mountedRef.current || !rendererRef.current || !sceneRef.current || !cameraRef.current) return;
    
    if (controlsRef.current) {
      controlsRef.current.update();
    }
    
    rendererRef.current.render(sceneRef.current, cameraRef.current);
    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    
    if (!containerRef.current) return;

    const scene = new THREE.Scene();
    sceneRef.current = scene;
    scene.background = new THREE.Color(0x18191a);

    const camera = new THREE.PerspectiveCamera(
      75,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true
    });
    rendererRef.current = renderer;
    
    if (containerRef.current.firstChild) {
      containerRef.current.removeChild(containerRef.current.firstChild);
    }
    
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controlsRef.current = controls;
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.autoRotate = !hasInteracted.current;
    controls.autoRotateSpeed = 2.0;

    controls.addEventListener('start', () => {
      if (!hasInteracted.current) {
        hasInteracted.current = true;
        controls.autoRotate = false;
      }
    });

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 1, 1);
    scene.add(directionalLight);

    const loader = new STLLoader();
    const fileUrl = URL.createObjectURL(file);
    
    loader.load(fileUrl, (geometry) => {
      if (!mountedRef.current) return;

      if (meshRef.current) {
        scene.remove(meshRef.current);
        meshRef.current.geometry.dispose();
        (meshRef.current.material as THREE.Material).dispose();
      }

      geometry.computeBoundingBox();
      const boundingBox = geometry.boundingBox;
      if (boundingBox && onVolumeCalculated && !volumeCalculatedRef.current) {
        const size = new THREE.Vector3();
        boundingBox.getSize(size);
        const volumeCm3 = (size.x * size.y * size.z) / 1000;
        onVolumeCalculated(volumeCm3);
        volumeCalculatedRef.current = true;
      }

      geometry.center();
      const material = new THREE.MeshStandardMaterial({
        color: 0x3b82f6,
        metalness: 0.3,
        roughness: 0.4,
      });
      
      const mesh = new THREE.Mesh(geometry, material);
      meshRef.current = mesh;
      mesh.rotation.x = -Math.PI / 2;
      scene.add(mesh);

      const box = new THREE.Box3().setFromObject(mesh);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = camera.fov * (Math.PI / 180);
      const cameraDistance = maxDim / (2 * Math.tan(fov / 2));

      camera.position.z = cameraDistance * 1.5;
      camera.lookAt(center);

      animate();
    });

    return () => {
      mountedRef.current = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      URL.revokeObjectURL(fileUrl);
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      if (meshRef.current) {
        scene.remove(meshRef.current);
        meshRef.current.geometry.dispose();
        (meshRef.current.material as THREE.Material).dispose();
      }
    };
  }, [file, animate]);

  return (
    <div ref={containerRef} className="w-full h-[300px] rounded-lg" />
  );
}

export default React.memo(STLViewer, (prevProps, nextProps) => {
  return prevProps.file === nextProps.file;
});